import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { Edit, Trash2, Eye, RefreshCw, Loader2, Heart, Clock } from "lucide-react"
import { BlogPost } from '@/services/other-services'

interface PostListProps {
    posts: BlogPost[]
    isLoading: boolean
    onEdit: (post: BlogPost) => void
    onDelete: (id: string) => void
    onView: (post: BlogPost) => void
    onBulkTranslate?: () => void
    isTranslating?: boolean
}
export function PostList({ posts, isLoading, onEdit, onDelete, onView, onBulkTranslate, isTranslating }: PostListProps) {
    return (
        <Card>
            <CardHeader className="pb-3">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <CardTitle>Tüm Yazılar</CardTitle>
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <Input placeholder="Yazı ara..." className="w-full sm:w-[200px]" />
                        {onBulkTranslate && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={onBulkTranslate}
                                disabled={isTranslating}
                                className="border-primary/20 hover:bg-primary hover:text-primary-foreground transition-all duration-300 whitespace-nowrap"
                            >
                                {isTranslating ? (
                                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                ) : (
                                    <RefreshCw className="mr-1 h-3 w-3" />
                                )}
                                Yazıları Çevir
                            </Button>
                        )}
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {isLoading ? (
                    <div className="flex h-32 items-center justify-center">Yükleniyor...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Başlık</TableHead>
                                    <TableHead>Yazar</TableHead>
                                    <TableHead>Kategoriler</TableHead>
                                    <TableHead>Metrikler</TableHead>
                                    <TableHead>Durum</TableHead>
                                    <TableHead className="text-right">İşlemler</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {posts.map((post) => (
                                    <TableRow key={post.id || post._id} className="group cursor-pointer">
                                        <TableCell className="font-medium group-hover:text-primary transition-colors">
                                            {typeof post.title === 'object' ? post.title.tr : post.title}
                                            <div className="text-[10px] text-muted-foreground font-normal">
                                                {new Date(post.createdAt).toLocaleDateString('tr-TR')}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {post.authorId?.name || 'Bilinmiyor'}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                                                {Array.isArray(post.categories) && post.categories.length > 0 ? (
                                                    post.categories.map((cat: any) => (
                                                        <Badge key={typeof cat === 'object' ? cat._id : cat} variant="outline" className="text-[10px] px-1.5 py-0 whitespace-nowrap">
                                                            {typeof cat === 'object' ? cat.name?.tr : cat}
                                                        </Badge>
                                                    ))
                                                ) : (
                                                    <span className="text-xs text-muted-foreground">Kategorisiz</span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                                <div className="flex items-center gap-1" title="Görüntülenme">
                                                    <Eye className="h-3 w-3" />
                                                    {post.views || 0}
                                                </div>
                                                <div className="flex items-center gap-1" title="Beğeni">
                                                    <Heart className="h-3 w-3" />
                                                    {post.likes || 0}
                                                </div>
                                                {(post.readingTime ?? 0) > 0 && (
                                                    <div className="flex items-center gap-1" title="Okunma Süresi">
                                                        <Clock className="h-3 w-3" />
                                                        <span>{post.readingTime} dk</span>
                                                    </div>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col gap-1">
                                                <Badge variant={post.isPublished ? "default" : "secondary"}>
                                                    {post.isPublished ? 'Yayında' : 'Taslak'}
                                                </Badge>
                                                {(post as any).isTranslating && (
                                                    <Badge variant="outline" className="text-[10px] gap-1 text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-900/20">
                                                        <Loader2 className="h-2.5 w-2.5 animate-spin" />
                                                        Çevriliyor...
                                                    </Badge>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="icon" onClick={() => onView(post)}><Eye className="h-4 w-4" /></Button>
                                            <Button variant="ghost" size="icon" onClick={() => onEdit(post)}><Edit className="h-4 w-4" /></Button>
                                            <Button variant="ghost" size="icon" onClick={() => onDelete(post.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </CardContent>
        </Card >
    )
}
